try {
    if (process.env.TOKENS_FEATURE_FLAG === 'true' || process.env.STORYBOOK_TOKENS_FEATURE_FLAG !== 'false') require('./index-new.css');
    else require('./index.css');
  } catch (e) {
    if (e.code !== 'MODULE_NOT_FOUND') {
      throw e; // Re-throw unexpected errors
    }
    console.warn('index.css / index-new.css not found, proceeding without it.');
  }


import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { Icon } from '@design-system/icon';
import { Tooltip } from '@design-system/tooltip';
import { Typography } from '@design-system/typography';
import { isBrowser } from '@phx-husky/browser-helpers';
import { useScreenRecognition } from '@phx-husky/use-screen-recognition';
import classNames from 'classnames';
import { forwardRef, useState, useEffect } from 'react';
import { Button } from '@design-system/button';
import { Link } from '@design-system/link';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
function __rest(s, e) {
  var t = {};

  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}
function __awaiter(thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function (resolve) {
      resolve(value);
    });
  }

  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }

    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }

    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }

    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
}

const ShareButton = ({ label, ariaLabel, shareContent, }) => (jsx(Button, Object.assign({ className: "cds-share-block__share-button", onClick: () => shareContent(), variant: "primary", "aria-label": ariaLabel, iconPrefix: { props: { icon: 'share' } } }, { children: label })));

const ShareList = ({ shareLinks, label, ariaLabel, shareContent, location, }) => (shareLinks === null || shareLinks === void 0 ? void 0 : shareLinks.length) ? (jsx("ul", Object.assign({ "aria-labelledby": "share-block", className: "cds-share-block__items" }, { children: shareLinks.map((_a) => {
        var { href, size = 'large', variant = 'subtle-icon' } = _a, linkItem = __rest(_a, ["href", "size", "variant"]);
        return (jsx("li", Object.assign({ className: "cds-share-block__item" }, { children: jsx(Link, Object.assign({ className: "cds-share-block__share-link", size: size, variant: variant, href: `${href}${location}` }, linkItem)) }), href));
    }) }))) : (jsx(ShareButton, { ariaLabel: ariaLabel, shareContent: shareContent, label: label }));

const ShareBlock = forwardRef(({ className, shareLinks, label, titleOptions = { variant: 'body', tag: 'p', size: 's' }, showCopyButton, copyButtonAriaLabel, shareButtonAriaLabel, copyButtonDefaultLabel, copyButtonActiveLabel, align = 'start', }, ref) => {
    const [location, setLocation] = useState('');
    const [tooltipContent, setTooltipContent] = useState(copyButtonDefaultLabel);
    const { isMobile } = useScreenRecognition();
    useEffect(() => setLocation(isBrowser() ? window.location.href : ''), []);
    const handleClickCopy = (text) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        try {
            setTooltipContent(copyButtonActiveLabel);
            return yield ((_a = navigator === null || navigator === void 0 ? void 0 : navigator.clipboard) === null || _a === void 0 ? void 0 : _a.writeText(text));
        }
        catch (e) {
            console.error(`Error while copy to clipboard`, e);
            return null;
        }
    });
    const shareContent = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            return yield (navigator === null || navigator === void 0 ? void 0 : navigator.share({
                url: location,
            }));
        }
        catch (e) {
            console.error(`Error while sharing content`, e);
            return null;
        }
    });
    return (jsx("div", Object.assign({ ref: ref, className: classNames('cds-share-block', className, `cds-share-block--align-${align}`), "data-testid": "cds-share-block" }, { children: !isMobile ? (jsxs(Fragment, { children: [(shareLinks === null || shareLinks === void 0 ? void 0 : shareLinks.length) ? (jsxs("div", Object.assign({ className: "cds-share-block__header" }, { children: [jsx(Icon, { icon: "share" }), label && (jsx(Typography, Object.assign({ className: "cds-share-block__title" }, Object.assign(Object.assign({}, titleOptions), { content: `${label}:` }))))] }))) : null, jsx(ShareList, { shareLinks: shareLinks, label: label, ariaLabel: shareButtonAriaLabel, location: location, shareContent: shareContent }), showCopyButton ? (jsx(Tooltip, { className: "cds-share-block__tooltip", label: copyButtonAriaLabel, portalQuerySelector: ".cds-tooltip", isTooltipContentInteractive: true, icon: "copy", onClickTooltip: () => handleClickCopy(location), onBlurTooltip: () => setTooltipContent(copyButtonDefaultLabel), content: jsx("span", { children: tooltipContent }) })) : null] })) : (jsx(ShareButton, { ariaLabel: shareButtonAriaLabel, shareContent: shareContent, label: label })) })));
});

export { ShareBlock };
